// @flow

import assign from 'lodash/assign';
import { logErrorToRaygun } from 'src/modules/raygun-logger';

import {
  dashboardUrl,
  candidateAccountUrl,
  saveLastSearchUrl,
  saveSearchUrl,
  jobMailUrl,
  saveJobUrl,
  savedSearchesUrl,
  fitmeUrl
} from 'src/config';
import {
  createAuthenticatedHttpClient,
  createUnauthenticatedHttpClient,
  localiseUrl
} from 'src/modules/seek-api-request';
import { parse as parseCookies } from 'cookie';

type SaveJobParams = {
  jobId: string,
  serpTracking: string,
  isSerp: Boolean,
  method?: string,
  locale?: Locale
};

const dashboard = ({
  locale,
  sections
}: {
  locale: Locale,
  sections: string
}) => {
  return createAuthenticatedHttpClient()
    .send({
      url: `${localiseUrl(locale, dashboardUrl)}${
        sections ? `?sections=${sections}` : ''
      }`,
      label: 'dashboard',
      timeout: 6000, // Avg 3200, so double that
      withCredentials: true
    })
    .then(({ data }) => data);
};

const account = () =>
  createAuthenticatedHttpClient()
    .send({
      url: candidateAccountUrl,
      timeout: 8000, // Avg 4000, so double it
      label: 'account'
    })
    .then(({ data }) => data);

const saveLastSearch = ({ locale }: { locale: Locale }) =>
  createUnauthenticatedHttpClient()
    .send({
      method: 'post',
      timeout: 6000, // Avg 2800, so double it
      url: localiseUrl(locale, saveLastSearchUrl),
      data: { cookieName: 'main' },
      label: 'save-last-search',
      withCredentials: true
    })
    .then(response => response.data);

const saveSearch = ({
  locale,
  query,
  location
}: {
  locale: Locale,
  query: SearchParams,
  location: RawSearchApiLocation
}) => {
  const { locationId, areaId, whereId } = location || {};

  const searchQuery = {
    ...query,
    location: locationId,
    area: areaId,
    whereid: whereId,
    expandLocation: false // added temporarily until jobseeker can be updated to make it optional
  };

  return createAuthenticatedHttpClient()
    .send({
      method: 'post',
      url: localiseUrl(locale, saveSearchUrl),
      data: searchQuery,
      label: 'save-search',
      timeout: 3000, // Avg 480, so use minimum of 3000
      withCredentials: true
    })
    .then(({ data }) => data);
};

const jobMail = ({
  locale,
  query,
  location,
  emailAddress
}: {
  locale: Locale,
  query: SearchParams,
  location: RawSearchApiLocation,
  emailAddress: string
}) => {
  const { locationId, areaId, whereId } = location || {};

  const searchQuery = {
    ...query,
    emailAddress,
    location: locationId,
    area: areaId,
    whereid: whereId
  };

  return createUnauthenticatedHttpClient()
    .send({
      method: 'post',
      url: `${localiseUrl(locale, jobMailUrl)}`,
      data: searchQuery,
      label: 'job-mail',
      timeout: 3000, // Avg 910, so use minimum of 3000
      withCredentials: true
    })
    .then(({ data }) => data);
};

function createSavedJobUri(jobId) {
  return `${saveJobUrl}/${jobId}`;
}

const getEventCaptureData = (serpTracking, isSerp) => {
  const eventCaptureData = {
    channel: 'web',
    view: isSerp ? 'serp' : 'jobDetails'
  };

  if (isSerp) {
    assign(eventCaptureData, { serpTracking });
  }

  return eventCaptureData;
};

const saveUnsaveJob = ({
  locale,
  jobId,
  serpTracking,
  isSerp,
  method = 'put'
}: SaveJobParams) => {
  const url = localiseUrl(locale, createSavedJobUri(jobId));
  const eventCaptureData = getEventCaptureData(serpTracking, isSerp);
  const sessionId = parseCookies(document.cookie).JobseekerSessionId;

  if (!sessionId) {
    logErrorToRaygun({
      error: new Error(
        'Save job event capture is not sent: session id is missing'
      ),
      customData: { cookie: document.cookie }
    });
  }

  const data = sessionId ? { eventCaptureData } : {};

  return createAuthenticatedHttpClient().send({
    method,
    url,
    data,
    timeout: 3000, // Avg remove 640 and save 1440, so use minimum of 3000
    label: method === 'put' ? 'save-job' : 'remove-save-job',
    withCredentials: true
  });
};

const saveJob = ({ locale, jobId, serpTracking, isSerp }: SaveJobParams) =>
  saveUnsaveJob({ locale, jobId, serpTracking, isSerp });
const removeSavedJob = ({
  locale,
  jobId,
  serpTracking,
  isSerp
}: SaveJobParams) =>
  saveUnsaveJob({ locale, jobId, serpTracking, isSerp, method: 'delete' });

const searchSavedJobs = ({ jobIds }: { jobIds: Array<string> }) => {
  return createAuthenticatedHttpClient()
    .send({
      method: 'post',
      url: saveJobUrl,
      data: { jobIds },
      timeout: 8000, // Avg 4500ms, so use maximum of 8000
      label: 'search-saved-jobs'
    })
    .then(({ data }) => data);
};

const getSavedSearches = (locale: Locale): Promise<SavedSearchesExperience> => {
  return createAuthenticatedHttpClient()
    .send({
      method: 'get',
      url: localiseUrl(locale, savedSearchesUrl),
      timeout: 8000,
      label: 'get-saved-searches',
      withCredentials: true
    })
    .then(({ data }) => data);
};

const getFitScore = ({
  jobId,
  params,
  locale
}: {
  jobId: number,
  params: FitMeParams,
  locale: Locale
}) =>
  createAuthenticatedHttpClient()
    .send({
      url: localiseUrl(locale, `${fitmeUrl}/${jobId}`),
      timeout: 8000,
      params,
      label: 'fitme'
    })
    .then(({ data }) => data);

export default {
  dashboard,
  getFitScore,
  account,
  saveLastSearch,
  saveSearch,
  jobMail,
  saveJob,
  removeSavedJob,
  searchSavedJobs,
  getSavedSearches
};
